import { FC, useCallback, useMemo, useReducer } from 'react';
import { Dispatch, bindActionCreators } from 'redux';

import { PlacementApplicationFormView } from './PlacementApplicationFormView';
import {
  setCompanyNameFieldValueAction,
  setCompanyTypeFieldValueAction,
  setEmailFieldValueAction,
  setPhoneFieldValueAction,
  validateAllAction,
  validateByFieldAction,
} from './actions';
import { EPlacementApplicationFormFields } from './constants';
import { getPlacementApplicationFormInitialState, placementApplicationFormReducer, validationRules } from './reducer';
import {
  CheckFormFieldInvalid,
  GetFormFieldErrorMessage,
  GetFormFieldValue,
  HandleBlur,
  HandleSubmit,
  ICoworkingPlacementApplicationFormProps,
} from './types';

export const CoworkingPlacementApplicationForm: FC<ICoworkingPlacementApplicationFormProps> = props => {
  // Покрыто функ. тестами
  /* istanbul ignore next */
  const { disableFieldsOnPending = false, formInitialState, onSubmit, pending = false } = props;

  const [formState, formDispatch] = useReducer(
    placementApplicationFormReducer,
    getPlacementApplicationFormInitialState(formInitialState),
  );

  const boundActions = useMemo(
    () =>
      bindActionCreators(
        {
          setCompanyNameFieldValueAction,
          setCompanyTypeFieldValueAction,
          setEmailFieldValueAction,
          setPhoneFieldValueAction,
          validateAllAction,
          validateByFieldAction,
        },
        formDispatch as Dispatch,
      ),
    [],
  );

  const handleSubmit = useCallback<HandleSubmit>(
    event => {
      event.preventDefault();

      const validationRulesKeys = Object.keys(validationRules) as Array<EPlacementApplicationFormFields>;

      // Покрыто функ. тестами
      /* istanbul ignore next */
      const formValuesValid = !validationRulesKeys.some(key => validationRules[key](formState[key].value, formState));

      if (formValuesValid) {
        onSubmit(formState);
      } else {
        boundActions.validateAllAction();
      }
    },
    [formState, onSubmit, boundActions],
  );

  // Покрыто функ. тестами
  /* istanbul ignore next */
  const handleBlur: HandleBlur = fieldName => () => boundActions.validateByFieldAction(fieldName);

  const getFormFieldErrorMessage = useCallback<GetFormFieldErrorMessage>(
    fieldType => formState[fieldType].errorMessage,
    [formState],
  );

  const checkFormFieldInvalid = useCallback<CheckFormFieldInvalid>(
    type => !!getFormFieldErrorMessage(type),
    [getFormFieldErrorMessage],
  );

  const getFormFieldValue: GetFormFieldValue = fieldType => formState[fieldType].value;

  return (
    <PlacementApplicationFormView
      boundActions={boundActions}
      checkFormFieldInvalid={checkFormFieldInvalid}
      disableFieldsOnPending={disableFieldsOnPending}
      getFormFieldErrorMessage={getFormFieldErrorMessage}
      getFormFieldValue={getFormFieldValue}
      pending={pending}
      onBlur={handleBlur}
      onSubmit={handleSubmit}
    />
  );
};

CoworkingPlacementApplicationForm.displayName = 'CoworkingPlacementApplicationForm';
